import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { Loader2, Plus, Trash2, FileDown, Wallet } from 'lucide-react'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'

const TABS = ['Positions', 'NAV & performance', 'Transactions'] as const
const TX_TYPES = ['buy', 'sell', 'dividend', 'fee', 'deposit', 'withdrawal']
const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const btn = 'flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50'
const btnPrimary = 'flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50'

const money = (v: any) => (v == null ? '—' : Math.round(Number(v)).toLocaleString())
const pct = (v: any) => (v == null ? '—' : `${(Number(v) * 100).toFixed(2)}%`)
const num = (v: any, d = 2) => (v == null ? '—' : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }))
const signed = (v: any) => (v == null ? 'text-gray-800' : Number(v) >= 0 ? 'text-emerald-600' : 'text-red-500')

export default function PortfolioBook() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<string>('Positions')
  const [pid, setPid] = useState('')
  const [showTx, setShowTx] = useState(false)
  const [tx, setTx] = useState<any>({ trade_date: '', type: 'buy', ticker: '', quantity: '', price: '', amount: '', fees: 0 })
  const [newName, setNewName] = useState('')

  const pfQ = useQuery({
    queryKey: ['pms-portfolios'],
    queryFn: () => api.get('/pms/portfolios').then(r => r.data.portfolios),
  })
  const portfolios = pfQ.data || []
  const activePid = pid || portfolios[0]?.id || ''

  const posQ = useQuery({
    queryKey: ['pms-positions', activePid],
    queryFn: () => api.get(`/pms/portfolios/${activePid}/positions`).then(r => r.data),
    enabled: !!activePid,
  })
  const navQ = useQuery({
    queryKey: ['pms-nav', activePid],
    queryFn: () => api.get(`/pms/portfolios/${activePid}/nav-series`).then(r => r.data),
    enabled: !!activePid && tab === 'NAV & performance',
  })
  const txQ = useQuery({
    queryKey: ['pms-tx', activePid],
    queryFn: () => api.get(`/pms/portfolios/${activePid}/transactions`).then(r => r.data.transactions),
    enabled: !!activePid,
  })
  const tickersQ = useQuery({
    queryKey: ['pms-tickers'],
    queryFn: () => api.get('/pms/tickers', { params: { limit: 200 } }).then(r => r.data.tickers),
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['pms-positions'] })
    qc.invalidateQueries({ queryKey: ['pms-nav'] })
    qc.invalidateQueries({ queryKey: ['pms-tx'] })
  }

  const s = posQ.data?.summary
  const perf = navQ.data?.performance

  const addPortfolio = async () => {
    if (!newName.trim()) return
    const r = await api.post('/pms/portfolios', { name: newName, base_currency: 'USD' })
    setNewName(''); setPid(r.data.id)
    qc.invalidateQueries({ queryKey: ['pms-portfolios'] })
  }

  const addTx = async () => {
    const body: any = { trade_date: tx.trade_date, type: tx.type, fees: Number(tx.fees || 0), currency: 'USD' }
    if (['buy', 'sell'].includes(tx.type)) {
      body.ticker = tx.ticker; body.quantity = Number(tx.quantity); body.price = Number(tx.price)
    } else {
      body.amount = Number(tx.amount)
      if (tx.ticker) body.ticker = tx.ticker
    }
    await api.post(`/pms/portfolios/${activePid}/transactions`, body)
    setTx({ trade_date: '', type: 'buy', ticker: '', quantity: '', price: '', amount: '', fees: 0 })
    setShowTx(false); refresh()
  }

  const delTx = async (id: string) => {
    if (!confirm('Delete this transaction?')) return
    await api.delete(`/pms/transactions/${id}`); refresh()
  }

  const exportXlsx = async () => {
    const r = await api.get(`/pms/portfolios/${activePid}/export.xlsx`, { responseType: 'blob' })
    const url = URL.createObjectURL(r.data); const a = document.createElement('a')
    a.href = url; a.download = 'portfolio-book.xlsx'; a.click(); URL.revokeObjectURL(url)
  }

  const isTrade = ['buy', 'sell'].includes(tx.type)
  const openPositions = (posQ.data?.positions || []).filter((p: any) => p.quantity > 0)

  if (pfQ.isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-4 p-1">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Portfolio Book</h1>
          <p className="text-sm text-gray-500">
            Holdings and performance derived from your transaction ledger
            {s ? ` · ${s.n_positions} positions · ${s.n_transactions} transactions` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className={inp} value={activePid} onChange={e => setPid(e.target.value)}>
            {portfolios.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input className={`${inp} w-36`} placeholder="new portfolio" value={newName} onChange={e => setNewName(e.target.value)} />
          <button className={btn} onClick={addPortfolio}><Plus size={15} />Add</button>
          <button className={btn} onClick={exportXlsx}><FileDown size={15} />Export</button>
          <button className={btnPrimary} onClick={() => setShowTx(v => !v)}><Plus size={15} />Transaction</button>
        </div>
      </div>

      {showTx && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-white p-3">
          <label className="text-xs text-gray-500">Date<input type="date" className={`${inp} ml-1`} value={tx.trade_date} onChange={e => setTx({ ...tx, trade_date: e.target.value })} /></label>
          <label className="text-xs text-gray-500">Type
            <select className={`${inp} ml-1`} value={tx.type} onChange={e => setTx({ ...tx, type: e.target.value })}>
              {TX_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          {isTrade ? (
            <>
              <label className="text-xs text-gray-500">Ticker
                <input className={`${inp} ml-1 w-32`} list="pms-tickers" value={tx.ticker} onChange={e => setTx({ ...tx, ticker: e.target.value })} />
                <datalist id="pms-tickers">{(tickersQ.data || []).map((t: any) => <option key={t.ticker} value={t.ticker} />)}</datalist>
              </label>
              <label className="text-xs text-gray-500">Qty<input type="number" className={`${inp} ml-1 w-24`} value={tx.quantity} onChange={e => setTx({ ...tx, quantity: e.target.value })} /></label>
              <label className="text-xs text-gray-500">Price<input type="number" className={`${inp} ml-1 w-28`} value={tx.price} onChange={e => setTx({ ...tx, price: e.target.value })} /></label>
            </>
          ) : (
            <>
              <label className="text-xs text-gray-500">Amount<input type="number" className={`${inp} ml-1 w-32`} value={tx.amount} onChange={e => setTx({ ...tx, amount: e.target.value })} /></label>
              <label className="text-xs text-gray-500">Ticker (opt)<input className={`${inp} ml-1 w-28`} value={tx.ticker} onChange={e => setTx({ ...tx, ticker: e.target.value })} /></label>
            </>
          )}
          <label className="text-xs text-gray-500">Fees<input type="number" className={`${inp} ml-1 w-20`} value={tx.fees} onChange={e => setTx({ ...tx, fees: e.target.value })} /></label>
          <button className={btnPrimary} disabled={!tx.trade_date} onClick={addTx}>Save</button>
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        <Kpi label="NAV" value={money(s?.nav)} icon={<Wallet size={14} />} />
        <Kpi label="Cash" value={money(s?.cash)} />
        <Kpi label="Securities" value={money(s?.securities_value)} />
        <Kpi label="Unrealised P&L" value={money(s?.unrealized_pnl)} cls={signed(s?.unrealized_pnl)} />
        <Kpi label="Realised P&L" value={money(s?.realized_pnl)} cls={signed(s?.realized_pnl)} />
        <Kpi label="Total P&L" value={money(s?.total_pnl)} cls={signed(s?.total_pnl)} />
      </div>

      <div className="flex flex-wrap gap-x-5 border-b border-gray-200 text-sm">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`-mb-px border-b-2 pb-2 pt-1 ${tab === t ? 'border-blue-600 font-medium text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>{t}</button>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        {tab === 'Positions' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead><tr>{['Ticker', 'Quantity', 'Avg cost', 'Last', 'Source', 'Market value', 'Cost basis', 'Unrealised', '%', 'Weight'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {openPositions.map((p: any) => (
                  <tr key={p.ticker} className="hover:bg-gray-50">
                    <td className={`${td} font-medium`}>{p.ticker}</td>
                    <td className={td}>{num(p.quantity)}</td>
                    <td className={td}>{num(p.avg_cost)}</td>
                    <td className={td}>{num(p.last_price)}</td>
                    <td className={`${td} text-xs ${p.priced_from === 'market' ? 'text-emerald-600' : 'text-amber-600'}`}>{p.priced_from}</td>
                    <td className={td}>{money(p.market_value)}</td>
                    <td className={td}>{money(p.cost_basis)}</td>
                    <td className={`${td} ${signed(p.unrealized_pnl)}`}>{money(p.unrealized_pnl)}</td>
                    <td className={`${td} ${signed(p.unrealized_pct)}`}>{p.unrealized_pct == null ? '—' : pct(p.unrealized_pct)}</td>
                    <td className={td}>{p.weight == null ? '—' : pct(p.weight)}</td>
                  </tr>
                ))}
                {!openPositions.length && <tr><td colSpan={10} className="px-3 py-8 text-center text-sm text-gray-400">No open positions — add a deposit and a buy to start the book.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'NAV & performance' && (
          <div className="space-y-4">
            {navQ.isFetching && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="animate-spin" size={15} />Rebuilding NAV from the ledger…</div>}
            {perf && (
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                <Kpi label="TWR (total)" value={pct(perf.twr_total)} cls={signed(perf.twr_total)} />
                <Kpi label="TWR (annualised)" value={pct(perf.twr_annualized)} cls={signed(perf.twr_annualized)} />
                <Kpi label="MWR (XIRR)" value={pct(perf.mwr_xirr)} cls={signed(perf.mwr_xirr)} />
                <Kpi label="Volatility (ann)" value={pct(perf.volatility_ann)} />
                <Kpi label="Max drawdown" value={pct(perf.max_drawdown)} cls="text-red-500" />
              </div>
            )}
            {navQ.data?.series?.length > 0 && (
              <>
                <p className="text-xs text-gray-500">
                  NAV is rebuilt day by day from the transaction ledger and market prices. TWR neutralises
                  deposits/withdrawals; MWR (XIRR) reflects the timing of your own cash flows.
                </p>
                <ResponsiveContainer width="100%" height={340}>
                  <AreaChart data={navQ.data.series} margin={{ top: 10, right: 20, bottom: 10, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={40} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `${Math.round(v / 1000)}k`} />
                    <Tooltip formatter={(v: number) => money(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area type="monotone" dataKey="nav" name="NAV" stroke="#2563eb" fill="#2563eb" fillOpacity={0.12} strokeWidth={2} />
                    <Area type="monotone" dataKey="cash" name="Cash" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.08} />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
            {!navQ.isFetching && !navQ.data?.series?.length && <Empty text="No NAV history yet — add transactions first." />}
          </div>
        )}

        {tab === 'Transactions' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead><tr>{['Date', 'Type', 'Ticker', 'Quantity', 'Price', 'Amount', 'Fees', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-100">
                {(txQ.data || []).map((t: any) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className={td}>{String(t.trade_date).slice(0, 10)}</td>
                    <td className={`${td} font-medium`}>{t.type}</td>
                    <td className={td}>{t.ticker || '—'}</td>
                    <td className={td}>{t.quantity == null ? '—' : num(t.quantity)}</td>
                    <td className={td}>{t.price == null ? '—' : num(t.price)}</td>
                    <td className={td}>{t.amount == null ? '—' : money(t.amount)}</td>
                    <td className={td}>{num(t.fees)}</td>
                    <td className={td}><button className="text-gray-400 hover:text-red-600" onClick={() => delTx(t.id)}><Trash2 size={14} /></button></td>
                  </tr>
                ))}
                {!(txQ.data || []).length && <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-gray-400">No transactions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

function Kpi({ label, value, cls, icon }: { label: string; value: any; cls?: string; icon?: any }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center gap-1 text-xs text-gray-400">{icon}{label}</div>
      <div className={`mt-1 text-lg font-semibold ${cls || 'text-gray-900'}`}>{value ?? '—'}</div>
    </div>
  )
}

function Empty({ text }: { text: string }) {
  return <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">{text}</div>
}
